import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import type { ComponentType } from 'react';

export type Sizing = 'hug' | 'fill';

// `hug` takes its natural width; `fill` shares the row (`flex: 1 1 auto`) and drops the
// flex-item default min-width so the text inside it can shrink enough to truncate
const SIZING: Record<Sizing, string> = {
  hug: '',
  fill: 'flex-auto min-w-0',
};

export const sizingClass = (sizing: Sizing | undefined): string =>
  SIZING[sizing ?? 'hug'];

export type TextStyleName =
  | 'headline'
  | 'title'
  | 'value'
  | 'body'
  | 'note'
  | 'meta';

// Every size reads a `--ac-*` variable from registry/cardScale.css, which the card's frame
// turns on. Static strings so Tailwind's build-time class scan finds them
export const TEXT_STYLE: Record<TextStyleName, string> = {
  headline:
    'text-[length:var(--ac-headline)] leading-[var(--ac-headline-lh)] font-medium',
  title:
    'text-[length:var(--ac-title)] leading-[var(--ac-title-lh)] font-semibold',
  value:
    'text-[length:var(--ac-title)] leading-[var(--ac-title-lh)] font-medium',
  body: 'text-[length:var(--ac-body)] leading-[var(--ac-body-lh)]',
  note: 'text-[length:var(--ac-note)] leading-[var(--ac-note-lh)]',
  meta: 'text-[length:var(--ac-meta)] leading-[var(--ac-meta-lh)]',
};

// A struck old title sits directly above the new one at note scale, so it doesn't
// look like a second heading
export const STRUCK_TITLE_CLASS = `block mb-[calc(2*var(--ac-u))] font-normal text-foreground-muted ${TEXT_STYLE.note}`;

// Label and Icon share this tone set. `warn`, `danger` and `success` are the app's
// existing radix-style colour scales; `act`, `agent`, `done` and `fyi` are the card
// kind tones, each its own `--kind-*` custom property in shell-theme.css; `rest` is a
// row title at rest, `--title-rest`.
export type Tone =
  | 'default'
  | 'muted'
  | 'subtle'
  | 'warn'
  | 'danger'
  | 'success'
  | 'act'
  | 'agent'
  | 'done'
  | 'fyi'
  | 'rest';

export const TONE_CLASS: Record<Tone, string> = {
  default: 'text-foreground',
  muted: 'text-foreground-muted',
  subtle: 'text-foreground-subtle',
  warn: 'text-warning-11',
  danger: 'text-danger-11',
  success: 'text-success-11',
  act: 'text-kind-act',
  agent: 'text-kind-agent',
  done: 'text-kind-done',
  fyi: 'text-kind-fyi',
  rest: 'text-title-rest',
};

// Prose colours its paragraphs and list items itself, so a tone must reach them directly
export const MARKDOWN_TONE_CLASS: Record<Tone, string> = {
  default: '[&_p]:!text-foreground [&_li]:!text-foreground',
  muted: '[&_p]:!text-foreground-muted [&_li]:!text-foreground-muted',
  subtle: '[&_p]:!text-foreground-subtle [&_li]:!text-foreground-subtle',
  warn: '[&_p]:!text-warning-11 [&_li]:!text-warning-11',
  danger: '[&_p]:!text-danger-11 [&_li]:!text-danger-11',
  success: '[&_p]:!text-success-11 [&_li]:!text-success-11',
  act: '[&_p]:!text-kind-act [&_li]:!text-kind-act',
  agent: '[&_p]:!text-kind-agent [&_li]:!text-kind-agent',
  done: '[&_p]:!text-kind-done [&_li]:!text-kind-done',
  fyi: '[&_p]:!text-kind-fyi [&_li]:!text-kind-fyi',
  rest: '[&_p]:!text-title-rest [&_li]:!text-title-rest',
};

export const isTone = (value: unknown): value is Tone =>
  typeof value === 'string' && Object.hasOwn(TONE_CLASS, value);

// The box every pill-shaped atom shares, so a date pill, a picker pill and a chip
// on one line stand the same height with the same inner spacing
export const PILL_BOX_CLASS =
  'h-[var(--ac-pill-h)] gap-[calc(6*var(--ac-u))] px-[calc(8*var(--ac-u))] text-[length:var(--ac-pill)] leading-[var(--ac-pill-lh)] font-medium';

// The property-pill trigger of a bound picker (Select's pill/segmented appearance,
// DateRange, DateInput, Button's pill appearance): a compact rounded pill with an
// icon slot, pill text and a hover tint
export const PILL_CLASS = `inline-flex max-w-full items-center rounded-full border border-border bg-transparent text-foreground transition-colors hover:bg-surface-hover ${PILL_BOX_CLASS}`;

export const GHOST_PILL_CLASS =
  'border-dashed border-border-strong text-foreground-muted hover:text-foreground';

// The object's footer (project, list, calendar) reads as plain muted text
export const QUIET_TEXT_CLASS = `inline-flex max-w-full min-w-0 items-center gap-[calc(6*var(--ac-u))] text-foreground-muted ${TEXT_STYLE.meta}`;

// A footer picker: the quiet text, with the pill's border only on hover, focus and
// while its menu is open. Negative margins cancel its padding, so the words line up
// with the object's edge
export const QUIET_PICKER_CLASS = `${QUIET_TEXT_CLASS} mx-[calc(-8*var(--ac-u))] my-[calc(-4*var(--ac-u))] h-auto rounded-full border border-transparent px-[calc(8*var(--ac-u))] py-[calc(3*var(--ac-u))] transition-colors hover:border-border hover:text-foreground focus-visible:border-border focus-visible:outline-none data-[state=open]:border-border`;

export const PILL_PLACEHOLDER_CLASS = 'text-foreground-muted';

// A field while a run is going: its value shows, locked and faded, until the run ends
export const HELD_CLASS = 'opacity-70';

export const PILL_ERROR_CLASS =
  'border-danger-8 ring-1 ring-danger-8 text-danger-11';

export type Gap = 'xs' | 'sm' | 'md' | 'lg';

export const GAP: Record<Gap, string> = {
  xs: 'gap-[var(--ac-gap-xs)]',
  sm: 'gap-[var(--ac-gap-sm)]',
  md: 'gap-[var(--ac-gap-md)]',
  lg: 'gap-[var(--ac-gap-lg)]',
};

// A wrapping row's lines sit closer than its items: row gap is about 0.75 × column gap
export const WRAP_GAP: Record<Gap, string> = {
  xs: 'gap-x-[var(--ac-gap-xs)] gap-y-[calc(3*var(--ac-u))]',
  sm: 'gap-x-[var(--ac-gap-sm)] gap-y-[calc(6*var(--ac-u))]',
  md: 'gap-x-[calc(12*var(--ac-u))] gap-y-[calc(9*var(--ac-u))]',
  lg: 'gap-x-[calc(14*var(--ac-u))] gap-y-[calc(6*var(--ac-u))]',
};

// TaskRow's check glyph is `--ac-check` with `GAP.sm` before the title.
// `INSET_CLASS.taskTitle` pads by that same sum, so Column's `inset` lines children up
// with the title edge; both read the same variables, so the two can't drift apart.
export const TASK_ROW_CHECK_SIZE_CLASS = 'size-[var(--ac-check)]';

export type Inset = 'taskTitle';

const INSET_CLASS: Record<Inset, string> = {
  taskTitle: 'pl-[calc(var(--ac-check)+var(--ac-gap-sm))]',
};

export const insetClass = (inset: Inset | undefined): string =>
  inset ? INSET_CLASS[inset] : '';

// Pulls the block under a task title up against the object's gap, so it sits 2px
// under the title and the two read as one block
export const UNDER_TITLE_PULL: Record<Gap, string> = {
  xs: 'mt-[calc(var(--ac-u)*2-var(--ac-gap-xs))]',
  sm: 'mt-[calc(var(--ac-u)*2-var(--ac-gap-sm))]',
  md: 'mt-[calc(var(--ac-u)*2-var(--ac-gap-md))]',
  lg: 'mt-[calc(var(--ac-u)*2-var(--ac-gap-lg))]',
};

export const ALIGN = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
} as const;

export const JUSTIFY = {
  start: 'justify-start',
  between: 'justify-between',
  end: 'justify-end',
} as const;

// Static class names so Tailwind generates them; a longer clamp uses the longest
export const LINE_CLAMP = [
  'line-clamp-1',
  'line-clamp-2',
  'line-clamp-3',
  'line-clamp-4',
  'line-clamp-5',
  'line-clamp-6',
  'line-clamp-7',
  'line-clamp-8',
  'line-clamp-9',
  'line-clamp-10',
] as const;

export const BADGE_TONE = {
  neutral: 'border-border bg-secondary text-foreground hover:bg-secondary',
  info: 'border-border-strong bg-surface-active text-foreground-subtle hover:bg-surface-active',
  success: 'border-success-5 bg-success-3 text-success-11 hover:bg-success-3',
  danger: 'border-danger-5 bg-danger-3 text-danger-11 hover:bg-danger-3',
} as const;

export const BADGE_CLASS = `max-w-full truncate px-[calc(8*var(--ac-u))] py-[calc(2*var(--ac-u))] font-medium shadow-none ${TEXT_STYLE.meta}`;

export const CHIP_CLASS = `border-border bg-transparent text-foreground shadow-none hover:bg-transparent ${PILL_BOX_CLASS}`;

export const NOTE_TONE = {
  info: {
    box: 'rounded-lg border border-blue-300/40 bg-blue-200/40 px-[calc(12*var(--ac-u))] py-[calc(8*var(--ac-u))] text-foreground dark:border-blue-900/40 dark:bg-blue-950/50',
    icon: 'text-blue-600 dark:text-blue-400',
  },
  danger: {
    box: 'rounded-lg border border-danger-5 bg-danger-3 px-[calc(12*var(--ac-u))] py-[calc(8*var(--ac-u))] text-danger-11',
    icon: 'text-danger-11',
  },
  muted: {
    box: 'text-foreground-muted',
    icon: 'text-foreground-muted',
  },
} as const;

export const NOTE_ICON: Record<
  keyof typeof NOTE_TONE,
  ComponentType<{ className?: string }> | null
> = {
  info: Icons.info,
  danger: Icons.warn,
  muted: null,
};

export const BUTTON_VARIANT = {
  primary: 'default',
  secondary: 'outline',
  ghost: 'ghost',
  danger: 'ghost',
  quiet: 'ghost',
} as const;

export const BUTTON_ICON: Record<
  | 'x'
  | 'send'
  | 'check'
  | 'retry'
  | 'open'
  | 'chevron'
  | 'plus'
  | 'stop'
  | 'continue',
  ComponentType<{ className?: string }>
> = {
  x: Icons.cancel,
  send: Icons.paperPlane,
  check: Icons.check,
  retry: Icons.rotateCw,
  open: Icons.arrowUpRight,
  chevron: Icons.chevronRight,
  plus: Icons.plus,
  stop: Icons.stop,
  continue: Icons.chevronsRight,
};

// The inline value box the card's fields use: borderless, a hover wash and a
// focus ring, pulled out by its own padding so text lines up with plain labels
export const TEXT_BOX_CLASS = `mx-[calc(-6*var(--ac-u))] block w-[calc(100%+12*var(--ac-u))] rounded-md border-0 px-[calc(6*var(--ac-u))] py-[calc(2*var(--ac-u))] text-left text-foreground outline-none break-words ${TEXT_STYLE.body}`;

export const FIELD_LABEL_CLASS =
  'min-w-[calc(70*var(--ac-u))] shrink-0 whitespace-nowrap text-[length:var(--ac-meta)] leading-[var(--ac-field-lh)] text-foreground-muted';

export const FIELD_ERROR_RING_CLASS = 'ring-1 ring-danger-8';

// `prose-sm` sets its own font size and line height; pin them back to the
// card's body style so a mrkdwn or markdown field reads at the same size as
// plain body text
export const MARKDOWN_CLASS = `prose prose-bird prose-sm max-w-none text-left text-foreground ${TEXT_STYLE.body}`;

export const IMAGE_SIZE = {
  sm: 'size-[calc(24*var(--ac-u))] rounded-md object-cover',
  md: 'size-[calc(40*var(--ac-u))] rounded-lg object-cover',
} as const;

export const ICON_CLASS = 'size-[var(--ac-icon)]';
export const ICON_SM_CLASS = 'size-[var(--ac-icon-sm)]';

export const BUTTON_TEXT_CLASS =
  'h-auto min-h-[var(--ac-button-h)] max-w-full whitespace-normal px-[var(--ac-button-px)] py-[calc(4*var(--ac-u))] text-[length:var(--ac-button)] leading-[var(--ac-button-lh)] font-medium';

export const OBJECT_INSET_CLASS =
  'rounded-[var(--ac-object-radius)] px-[var(--ac-pad-x)] py-[var(--ac-pad-y)]';

// A hairline above every shown child but the first; hidden children leave no
// element, so no two lines ever meet
export const DIVIDED_CLASS = '[&>*+*]:border-t [&>*+*]:border-border-subtle';

// Without flush the gap splits around each hairline, so a row never touches the
// line below it
export const DIVIDED_GAP: Record<Gap, string> = {
  xs: '[&>*+*]:mt-[calc(var(--ac-gap-xs)/2)] [&>*+*]:pt-[calc(var(--ac-gap-xs)/2)]',
  sm: '[&>*+*]:mt-[calc(var(--ac-gap-sm)/2)] [&>*+*]:pt-[calc(var(--ac-gap-sm)/2)]',
  md: '[&>*+*]:mt-[calc(var(--ac-gap-md)/2)] [&>*+*]:pt-[calc(var(--ac-gap-md)/2)]',
  lg: '[&>*+*]:mt-[calc(var(--ac-gap-lg)/2)] [&>*+*]:pt-[calc(var(--ac-gap-lg)/2)]',
};

// A disclosure's trigger at meta scale, muted until hovered
export const DETAILS_TRIGGER_CLASS = cn(
  'group flex w-full min-w-0 items-center gap-[var(--ac-gap-xs)] text-left transition-colors duration-[120ms] hover:text-foreground motion-reduce:duration-[60ms]',
  TEXT_STYLE.meta,
  TONE_CLASS.muted
);
